import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import TabHeader from '../../Components/TabHeader/TabHeader';
import { useClient } from '../../../../api/api-client';
import {
  POLICY_LEVEL,
  POLICY_STATUS,
  COMPLAINT_TYPE_POLICY_ENDPOINTS,
} from './constants';
import PolicyLevelSelector from './Elements/PolicyLevelSelector';
import PolicyParameterForm from './Elements/PolicyParameterForm';
import PolicyResult from './Elements/PolicyResult';

const ComplaintTypePolicy = () => {
  const client = useClient();

  const [selectedLevel, setSelectedLevel] = useState(POLICY_LEVEL.ITEM);

  // Order level fields
  const [orderTag, setOrderTag] = useState('');

  // Item level fields
  const [itemTag, setItemTag] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemAttrId, setItemAttrId] = useState('');
  const [itemAttrValue, setItemAttrValue] = useState('');

  // Status check result
  const [policyStatus, setPolicyStatus] = useState(POLICY_STATUS.NONE);
  const [policyDetails, setPolicyDetails] = useState(null);

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Update mode
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Complaint type dropdowns
  const [includedCTypes, setIncludedCTypes] = useState([]);
  const [excludedCTypes, setExcludedCTypes] = useState([]);

  // Complaint type options fetched from backend
  const [complaintTypeOptions, setComplaintTypeOptions] = useState([]);

  const buildRequestBody = useCallback(() => {
    const requestBody = {
      policy_level: selectedLevel,
    };
    if (selectedLevel === POLICY_LEVEL.ORDER) {
      requestBody.order_policy_parameter = {
        tag: orderTag.trim(),
      };
    } else {
      const itemParams = {};
      if (itemTag.trim()) itemParams.tag = itemTag.trim();
      if (itemCategory.trim()) itemParams.category = itemCategory.trim();
      if (itemAttrId.trim()) itemParams.attribute_id = itemAttrId.trim();
      if (itemAttrValue.trim())
        itemParams.attribute_value = itemAttrValue.trim();
      requestBody.item_policy_parameter = itemParams;
    }

    return requestBody;
  }, [selectedLevel, orderTag, itemTag, itemCategory, itemAttrId, itemAttrValue]);
  //resets result section
  const resetStatusState = useCallback(() => {
    setPolicyStatus(POLICY_STATUS.NONE);
    setPolicyDetails(null);
    setIsUpdateMode(false);
    setShowDeleteConfirm(false);
    setIncludedCTypes([]);
    setExcludedCTypes([]);
  }, []);
  //resets result + clear input
  const resetAllFields = useCallback(() => {
    setOrderTag('');
    setItemTag('');
    setItemCategory('');
    setItemAttrId('');
    setItemAttrValue('');
    resetStatusState();
  }, [resetStatusState]);

  const handleTabClick = useCallback(
    (key) => {
      setSelectedLevel(key);
      setOrderTag('');
      setItemTag('');
      setItemCategory('');
      setItemAttrId('');
      setItemAttrValue('');
      resetStatusState();
    },
    [resetStatusState],
  );

  // Fetch policy details after status check confirms existence
  const fetchPolicyDetails = useCallback(
    async (requestBody) => {
      try {
        const response = await client(
          COMPLAINT_TYPE_POLICY_ENDPOINTS.FETCH,
          { data: requestBody },
        );
        setPolicyDetails(response);
      } catch (error) {
        toast.error('Failed to fetch policy details');
        throw error;
      }
    },
    [client],
  );

  // Status check on submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedLevel === POLICY_LEVEL.ORDER) {
      if (!orderTag.trim()) {
        toast.error('Tag is required for Order level policy');
        return;
      }
    }

    if (selectedLevel === POLICY_LEVEL.ITEM) {
      if (
        !itemTag.trim() &&
        !itemCategory.trim() &&
        !itemAttrId.trim() &&
        !itemAttrValue.trim()
      ) {
        toast.error('At least one parameter is required for Item level policy');
        return;
      }
    }

    const requestBody = buildRequestBody();
    setIsSubmitting(true);
    resetStatusState();

    try {
      const statusResponse = await client(
        COMPLAINT_TYPE_POLICY_ENDPOINTS.STATUS,
        { data: requestBody },
      );

      const { exists, is_deleted, allowed_complaint_types } = statusResponse;

      if (allowed_complaint_types) {
        setComplaintTypeOptions(
          allowed_complaint_types.map((name) => ({ id: name, name }))
        );
      }
      if (exists && !is_deleted) {
        try {
          await fetchPolicyDetails(requestBody);
          setPolicyStatus(POLICY_STATUS.EXISTS);
          toast.success('Policy found');
        } catch {
          // fetchPolicyDetails already showed error toast, don't set EXISTS
        }
      } else if (exists && is_deleted) {
        setPolicyStatus(POLICY_STATUS.DELETED);
      } else {
        setPolicyStatus(POLICY_STATUS.NOT_FOUND);
        toast('Policy does not exist. You can create a new one.');
      }
    } catch (error) {
      toast.error('Failed to check policy status');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create policy
  const handleCreate = async () => {
    const requestBody = buildRequestBody();
    if (includedCTypes.length > 0) {
      requestBody.included_complaint_types = includedCTypes.map(
        (ct) => ct.name,
      );
    }
    if (excludedCTypes.length > 0) {
      requestBody.excluded_complaint_types = excludedCTypes.map(
        (ct) => ct.name,
      );
    }

    setIsActionLoading(true);
    try {
      await client(COMPLAINT_TYPE_POLICY_ENDPOINTS.CREATE, {
        data: requestBody,
      });
      toast.success('Policy created successfully');
      resetAllFields();
    } catch (error) {
      toast.error('Failed to create policy');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Update policy
  const handleUpdate = async () => {
    const requestBody = buildRequestBody();
    requestBody.included_complaint_types = includedCTypes.map(
      (ct) => ct.name,
    );
    requestBody.excluded_complaint_types = excludedCTypes.map(
      (ct) => ct.name,
    );

    setIsActionLoading(true);
    try {
      await client(COMPLAINT_TYPE_POLICY_ENDPOINTS.UPDATE, {
        data: requestBody,
        method: 'PUT',
      });
      toast.success('Policy updated successfully');
      setIsUpdateMode(false);
      resetAllFields();
    } catch (error) {
      console.error('Update policy error:', error);
      toast.error(error?.message || error?.detail || 'Failed to update policy');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Delete policy
  const handleDelete = async () => {
    const requestBody = buildRequestBody();

    setIsActionLoading(true);
    try {
      await client(COMPLAINT_TYPE_POLICY_ENDPOINTS.DELETE, {
        data: requestBody,
        method: 'DELETE',
      });
      toast.success('Policy deleted successfully');
      resetAllFields();
    } catch (error) {
      console.error('Delete policy error:', error);
      toast.error(error?.message || error?.detail || 'Failed to delete policy');
    } finally {
      setIsActionLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Restore (respawn) policy
  const handleRestore = async () => {
    const requestBody = buildRequestBody();

    setIsActionLoading(true);
    try {
      await client(COMPLAINT_TYPE_POLICY_ENDPOINTS.RESPAWN, {
        data: requestBody,
        method: 'PUT',
      });
      toast.success('Policy restored successfully');
      resetAllFields();
    } catch (error) {
      console.error('Restore policy error:', error);
      toast.error(error?.message || error?.detail || 'Failed to restore policy');
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10'>
      <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-p-8 tw-rounded-2xl'>
        {/* Header */}
        <TabHeader
          title='Complaint Type Policies'
          subTitle='Manage complaint type policies at Order or Item level'
        />

        {/* Policy Level Tabs */}
        <PolicyLevelSelector
          selectedLevel={selectedLevel}
          handleTabClick={handleTabClick}
        />

        {/* Policy Parameter Fields */}
        <PolicyParameterForm
          selectedLevel={selectedLevel}
          orderTag={orderTag}
          setOrderTag={setOrderTag}
          itemTag={itemTag}
          setItemTag={setItemTag}
          itemCategory={itemCategory}
          setItemCategory={setItemCategory}
          itemAttrId={itemAttrId}
          setItemAttrId={setItemAttrId}
          itemAttrValue={itemAttrValue}
          setItemAttrValue={setItemAttrValue}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
        />
      </div>

      {/* Policy Result Section */}
      <PolicyResult
        policyStatus={policyStatus}
        policyDetails={policyDetails}
        isSubmitting={isSubmitting}
        isUpdateMode={isUpdateMode}
        setIsUpdateMode={setIsUpdateMode}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        includedCTypes={includedCTypes}
        setIncludedCTypes={setIncludedCTypes}
        excludedCTypes={excludedCTypes}
        setExcludedCTypes={setExcludedCTypes}
        complaintTypeOptions={complaintTypeOptions}
        isActionLoading={isActionLoading}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onRestore={handleRestore}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default ComplaintTypePolicy;
